import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

import { styles } from "../../pages/Tracks/styles";
import {
  ArtWorkAndCollaboratorsContainer,
  AudioPlayerTime,
  CloseButton,
  OuterPlayerContainer,
  PlayerContainer,
  AudioPlayerMainContainer,
  audioPlayerStyles,
} from "./styles";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { AudioPlayerProps } from "./types";
import { useAuth0 } from "@auth0/auth0-react";
import { Collaborators } from "./Collaborators";
import AddCommentModal from "../Modal/AddCommentModal";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { handleGetTrackComments } from "../../services/commentsService";
import {
  AudioComment,
  GetTrackTimeStampCommentsResponse,
  Role,
} from "../../proto/api_pb";
import TrackComments from "../Modal/TrackComments";
import { formatTime } from "./helper";
import WaveSurfer from "wavesurfer.js";

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  trackToPlay,
  isPlayButtonClicked,
  versionToPlay,
  isTrackForPlay,
  audioDetails,
  setTrackToPlay,
  setIsPlayButtonClicked,
  setVersionToPlay,
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const tempContainerRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [allComment, setAllComments] = useState<AudioComment>();

  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const { isAuthenticated } = useAuth0();
  const { enterprise, isEnterpriseAccount } = useContext(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const audioSrc = isTrackForPlay
    ? trackToPlay?.url
    : versionToPlay?.track?.getTrackUrl();

  const handleGetComments = useCallback(async () => {
    const token = await getValidAccessToken();
    const res = await handleGetTrackComments(
      token,
      isTrackForPlay
        ? (trackToPlay?.track?.getId() ?? "")
        : (versionToPlay?.track?.getTrackId() ?? ""),
      isTrackForPlay
        ? (trackToPlay?.track?.getTrackVersionId() ?? "")
        : (versionToPlay?.track?.getId() ?? "")
    );
    if (res instanceof GetTrackTimeStampCommentsResponse) {
      setAllComments(res.getAudioComment());
    }
  }, [
    getValidAccessToken,
    isTrackForPlay,
    trackToPlay?.track,
    versionToPlay?.track,
  ]);

  const refreshComments = async () => {
    await handleGetComments();
  };

  const handlePauseTrack = useCallback(() => {
    waveSurferRef.current?.pause();

    setIsPlaying(false);
    if (trackToPlay && setTrackToPlay) {
      setTrackToPlay({ ...trackToPlay, isPlaying: false });
    } else if (versionToPlay && setVersionToPlay) {
      setVersionToPlay({ ...versionToPlay, isPlaying: false });
    }
  }, [trackToPlay, setTrackToPlay, versionToPlay, setVersionToPlay]);

  const handlePlayTrack = useCallback(async () => {
    if (!waveSurferRef.current) {
      console.warn("WaveSurfer not initialized");
      return;
    }
    
    const duration = waveSurferRef.current.getDuration();
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (!isMobile && (!duration || duration === 0 || !isFinite(duration))) {
      console.warn("Audio not loaded yet, duration:", duration);
      return;
    }
    
    try {
      const playPromise = waveSurferRef.current.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      setIsPlaying(true);
      if (trackToPlay && setTrackToPlay) {
        setTrackToPlay({ ...trackToPlay, isPlaying: true });
      } else if (versionToPlay && setVersionToPlay) {
        setVersionToPlay({ ...versionToPlay, isPlaying: true });
      }
    } catch (error) {
      console.error("Play failed:", error);
      setIsPlaying(false);
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }
      
      if (isMobile && audioSrc) {
        console.log("Attempting to reload audio on mobile...");
        setTimeout(() => {
          if (waveSurferRef.current && audioSrc) {
            try {
              waveSurferRef.current.load(audioSrc);
            } catch (reloadError) {
              console.error("Failed to reload audio:", reloadError);
            }
          }
        }, 500);
      }
    }
  }, [trackToPlay, setTrackToPlay, versionToPlay, setVersionToPlay, audioSrc]);

  const handlePlayPause = useCallback(async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isPlaying) {
      handlePauseTrack();
    } else {
      await handlePlayTrack();
    }
  }, [isPlaying, handlePlayTrack, handlePauseTrack]);

  useEffect(() => {
    const shouldBePlaying = trackToPlay?.isPlaying || versionToPlay?.isPlaying;

    if (shouldBePlaying && !isPlaying && waveSurferRef.current) {
      if (!isAudioLoading) {
        handlePlayTrack();
      }
    } else if (!shouldBePlaying && isPlaying) {
      handlePauseTrack();
    }
  }, [
    isPlaying,
    trackToPlay?.isPlaying,
    versionToPlay?.isPlaying,
    handlePlayTrack,
    handlePauseTrack,
    isAudioLoading,
  ]);

  useEffect(() => {
    if (!audioSrc) {
      console.warn("Missing audio source");
      return;
    }

    if (typeof audioSrc !== 'string' || (!audioSrc.startsWith('http') && !audioSrc.startsWith('blob') && !audioSrc.startsWith('/'))) {
      console.error("Invalid audio source:", audioSrc);
      setIsAudioLoading(false);
      return;
    }

    setIsAudioLoading(true);

    if (waveSurferRef.current) {
      waveSurferRef.current.destroy();
      waveSurferRef.current = null;
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    const container = waveformRef.current;
    
    if (!container) {
      tempContainerRef.current = document.createElement('div');
      tempContainerRef.current.style.position = 'absolute';
      tempContainerRef.current.style.left = '-9999px';
      tempContainerRef.current.style.width = '1px';
      tempContainerRef.current.style.height = '1px';
      document.body.appendChild(tempContainerRef.current);
    }
    
    const waveSurfer = WaveSurfer.create({
      container: container || tempContainerRef.current!,
      waveColor: "#a0a0a0",
      progressColor: "#1976d2",
      height: 60,
      barWidth: 2,
    });

    console.log("Loading audio source:", audioSrc, "isMobile:", isMobile);
    
    const loadAudio = async () => {
      try {
        if (isMobile && audioSrc.startsWith('http')) {
          try {
            const token = await getValidAccessToken();
            const headers: HeadersInit = {
              'Accept': 'audio/*',
            };
            
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(audioSrc, {
              method: 'GET',
              headers: headers,
              credentials: 'include',
            });
            
            if (!response.ok) {
              const errorText = await response.text().catch(() => '');
              console.error(`Failed to fetch audio: ${response.status} ${response.statusText}`, errorText);
              throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
            }
            
            const contentType = response.headers.get('content-type');
            console.log("Audio response content-type:", contentType);
            
            const blob = await response.blob();
            console.log("Blob created, size:", blob.size, "type:", blob.type);
            
            if (blob.size === 0) {
              throw new Error("Received empty audio blob");
            }
            
            const blobUrl = URL.createObjectURL(blob);
            console.log("Created blob URL for mobile:", blobUrl);
            waveSurfer.load(blobUrl);
            
            waveSurfer.on("destroy", () => {
              URL.revokeObjectURL(blobUrl);
            });
          } catch (fetchError) {
            console.warn("Failed to fetch as blob, trying direct load:", fetchError);
            try {
              waveSurfer.load(audioSrc);
            } catch (directLoadError) {
              console.error("Direct load also failed:", directLoadError);
              setIsAudioLoading(false);
            }
          }
        } else {
          waveSurfer.load(audioSrc);
        }
      } catch (error) {
        console.error("Failed to load audio:", error);
        setIsAudioLoading(false);
      }
    };
    
    loadAudio();

    waveSurfer.on("ready", () => {
      const duration = waveSurfer.getDuration();
      setDuration(duration);
      setIsAudioLoading(false);
      
      console.log("WaveSurfer ready, duration:", duration, "isMobile:", isMobile);

      const shouldAutoplay =
        !isMobile &&
        (trackToPlay?.isPlaying || versionToPlay?.isPlaying) &&
        isPlayButtonClicked;

      if (shouldAutoplay) {
        waveSurfer.play().catch((error) => {
          console.warn("Autoplay failed:", error);
          setIsPlaying(false);
        });
        setIsPlaying(true);
      } else {
        setIsPlaying(false);
      }
    });
    
    waveSurfer.on("load", () => {
      console.log("WaveSurfer load event fired");
    });
    
    waveSurfer.on("loading", (progress) => {
      console.log("WaveSurfer loading progress:", progress);
    });

    waveSurfer.on("play", () => {
      setIsPlaying(true);
    });

    waveSurfer.on("pause", () => {
      setIsPlaying(false);
    });

    waveSurfer.on("audioprocess", () => {
      setCurrentTime(waveSurfer.getCurrentTime());
    });

    waveSurfer.on("interaction", () => {
      setCurrentTime(waveSurfer.getCurrentTime());
    });

    waveSurfer.on("error", (error) => {
      console.error("WaveSurfer error:", error);
      setIsAudioLoading(false);
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }
    });
    
    waveSurfer.on("decode", () => {
      console.log("WaveSurfer decode event fired");
    });

    waveSurferRef.current = waveSurfer;

    return () => {
      waveSurfer.destroy();
      if (tempContainerRef.current && tempContainerRef.current.parentNode) {
        document.body.removeChild(tempContainerRef.current);
        tempContainerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc, isPlayButtonClicked]);

  useEffect(() => {
    setAllComments(new AudioComment());
    if (trackToPlay || versionToPlay) handleGetComments();
  }, [handleGetComments, trackToPlay, versionToPlay]);

  const imageUrl = isTrackForPlay
    ? trackToPlay?.track?.getImageUrl()
    : audioDetails?.image;

  return (
    isPlayButtonClicked && (
      <AudioPlayerMainContainer
        style={!isAuthenticated ? { width: "100%" } : {}}
      >
        <Box sx={audioPlayerStyles.imageTitleContainer}>
          <ArtWorkAndCollaboratorsContainer>
            <img
              style={styles.image}
              src={
                imageUrl && imageUrl.includes("http")
                  ? imageUrl
                  : isEnterpriseAccount
                    ? enterprise?.image
                    : "/assets/images/logo512.png"
              }
              alt=""
            />
          </ArtWorkAndCollaboratorsContainer>
          <Typography data-testid="audio-title" sx={{ minWidth: "100px" }}>
            {isTrackForPlay
              ? trackToPlay?.track?.getTitle()
              : audioDetails?.title}
          </Typography>
        </Box>

        <OuterPlayerContainer>
          <ArtWorkAndCollaboratorsContainer>
            {trackToPlay?.track && (
              <Collaborators
                collaborators={trackToPlay?.track.getCollaboratorsList()}
                isGridView={true}
              />
            )}
          </ArtWorkAndCollaboratorsContainer>

          <Box sx={{ display: "flex" }}>
            {isEnterpriseAccount &&
              (enterprise?.role !== Role.ROLE_ARTIST.toString() ||
                trackToPlay?.track
                  ?.getCollaboratorsList()
                  .some((c) => c.getUserId() === userId)) && (
                <AddCommentModal
                  trackId={
                    isTrackForPlay
                      ? (trackToPlay?.track?.getId() ?? "")
                      : (versionToPlay?.track?.getTrackId() ?? "")
                  }
                  versionId={
                    isTrackForPlay
                      ? (trackToPlay?.track?.getTrackVersionId() ?? "")
                      : (versionToPlay?.track?.getId() ?? "")
                  }
                  commentTime={currentTime}
                  refreshComments={refreshComments}
                  pauseTrack={handlePauseTrack}
                  playTrack={handlePlayTrack}
                />
              )}
            {isEnterpriseAccount && <TrackComments allComment={allComment} />}
          </Box>

          <PlayerContainer>
            {isAudioLoading ? (
              <CircularProgress sx={{ color: "white" }} size={22} />
            ) : (
              <IconButton
                data-testid={isPlaying ? "pause-button" : "play-button"}
                onClick={handlePlayPause}
                onTouchStart={(e) => {
                  e.preventDefault();
                }}
              >
                {isPlaying ? (
                  <PauseCircleOutlineOutlinedIcon sx={{ fontSize: 30 }} />
                ) : (
                  <PlayCircleOutlineIcon sx={{ fontSize: 30 }} />
                )}
              </IconButton>
            )}
          </PlayerContainer>

          {!isSmallScreen && (
            <Box sx={{ width: "100%", marginLeft: "1rem" }}>
              <div ref={waveformRef} />
            </Box>
          )}

          <AudioPlayerTime>
            {formatTime(currentTime)} / {formatTime(duration)}
          </AudioPlayerTime>
        </OuterPlayerContainer>

        <Tooltip title="Close">
          <CloseButton
            data-testid="close-button"
            sx={isSmallScreen ? { marginRight: -2 } : {}}
            onClick={() => {
              waveSurferRef.current?.pause();
              if (isTrackForPlay) {
                setTrackToPlay((prev) => ({
                  ...prev!,
                  isPlaying: false,
                }));
              } else {
                if (setVersionToPlay) {
                  setVersionToPlay((prev) => ({
                    ...prev!,
                    isPlaying: false,
                    isSelected: true,
                  }));
                }
              }
              setIsPlayButtonClicked(false);
            }}
          >
            <CloseIcon />
          </CloseButton>
        </Tooltip>
      </AudioPlayerMainContainer>
    )
  );
};
